import dayjs from '~/timeInit';

import { LUX_LOYALTY_TIERS_CONFIG } from '@luxuryescapes/lib-lux-loyalty';

import { ROLE_ADMIN_USER, ROLE_LUX_LOYALTY_ADMIN } from '~/consts/roles';

import { Payment } from '~/types/services/payment';

import { EmptyArray } from './arrayUtils';
import featureToggle from './featureToggle';

export function isLuxLoyaltyEnabled(): boolean {
  return featureToggle.availableToShow('SHOW_LUX_LOYALTY');
}

export function canManageInfiniteMember(user: Pick<App.User, 'roles'> | null | undefined): boolean {
  return !!user?.roles.includes(ROLE_ADMIN_USER) || !!user?.roles.includes(ROLE_LUX_LOYALTY_ADMIN);
}

/**
 * Returns a friendly display name for any LuxLoyaltyTier. Falls back to a
 * capitalised slug when the published lib doesn't yet have a config entry for
 * the tier (e.g. the new `infinite` tier prior to the lib bump).
 */
export function getLuxLoyaltyTierDisplayName(tier: App.LuxLoyaltyTier): string {
  const config = (LUX_LOYALTY_TIERS_CONFIG as Partial<Record<App.LuxLoyaltyTier, { displayName: string }>>)[tier];
  return config?.displayName ?? tier.charAt(0).toUpperCase() + tier.slice(1);
}

/** Mirrors svc-lux-loyalty `getMembershipYearEndDate` for display when the API field is absent. */
export function getMembershipYearEndDate(joinDate: string | Date, format = 'YYYY-MM-DD'): string {
  const today = dayjs();
  const targetDate = dayjs(joinDate);

  const todayMonth = today.month();
  const targetMonth = targetDate.month();
  const todayYear = today.year();
  const targetYear = targetDate.year();
  const targetDateThisYear = targetDate.year(today.year());

  if (targetMonth < todayMonth || (targetMonth === todayMonth && todayYear === targetYear)) {
    return targetDateThisYear.add(1, 'year').endOf('month').format(format);
  }

  return targetDateThisYear.endOf('month').format(format);
}

export interface MembershipYearInfo {
  yearNumber: number;
  startDate: string;
  endDate: string;
}

export function getMembershipYearInfo(createdAt: string, membershipYearEndDate: string): MembershipYearInfo {
  const join = dayjs(createdAt).startOf('day');
  const end = dayjs(membershipYearEndDate).startOf('day');

  // Year 1 ends on the last day of the join month one calendar year after joining.
  const firstYearEnd = join.add(1, 'year').endOf('month').startOf('day');
  let yearNumber = 1;
  let cursor = firstYearEnd;
  while (cursor.isBefore(end, 'day')) {
    yearNumber += 1;
    cursor = cursor.add(1, 'year').endOf('month').startOf('day');
  }

  const start = yearNumber === 1 ? join : end.subtract(1, 'year').endOf('month').add(1, 'day').startOf('day');

  return {
    yearNumber,
    startDate: start.format('YYYY-MM-DD'),
    endDate: membershipYearEndDate,
  };
}

export interface ItemPointsPaymentDetails {
  itemId: string;
  currency: string;
  pointsPaid: number;
  pointsPaidCurrencyValue: number;
  priceInCurrency: number;
  priceInPoints: number;
}

/**
 * Extracts the points payments for an item
 */
export function filterPointsPaymentsForItem(
  /** Id of the item for which to find points payments */
  itemId: string,
  /** The array of payments associated with an order */
  payments: Array<Payment>,
): Array<ItemPointsPaymentDetails> {
  const payment = payments.find((payment) => payment.type === 'le_points' && payment.item_ids.includes(itemId));
  if (!payment) {
    return EmptyArray;
  }
  const itemsBreakdown = payment.items_breakdown?.find((breakdown) => breakdown?.id_items === itemId);
  if (!itemsBreakdown) {
    return EmptyArray;
  }
  const pointsDetails = itemsBreakdown?.item_le_points_details;
  if (!pointsDetails) {
    return EmptyArray;
  }

  return [
    {
      itemId: itemsBreakdown.item_le_points_details?.item_id,
      currency: payment.currency,
      pointsPaid: itemsBreakdown.item_le_points_details?.points_paid,
      pointsPaidCurrencyValue: itemsBreakdown.item_le_points_details?.points_paid_currency_value,
      priceInCurrency: itemsBreakdown.item_le_points_details?.price_in_currency,
      priceInPoints: itemsBreakdown.item_le_points_details?.price_in_points,
    },
  ];
}

/**
 * Extracts the pending points for an item from all pending points associated with an order
 * @returns Pending points for the item or undefined
 */
export function filterPendingPointsForItem(
  /** Id of the item for which to find pending points */
  itemId: string,
  /** Pending points returned from request to lux loyalty */
  pendingPoints: Array<App.LuxLoyaltyPendingPoint>,
): Array<App.LuxLoyaltyPendingPoint> {
  return pendingPoints.filter((pending) => pending.itemId === itemId);
}
export const luxLoyaltyBenefitDescriptionMap: Map<App.LuxLoyaltyBenefitType, string> = new Map([
  ['airport_transfer', 'Airport Transfers'],
  ['discounted_insurance', 'Discounted Travel & Booking Protection'],
  ['early_access', 'Early Access To Deals'],
  ['hidden_access', 'Hidden Access To Deals'],
  ['lounge_pass', 'Lounge Pass Access'],
  ['phone_support', 'Priority 24/7 Phone Support'],
  ['tour_exp_credit', 'Tour Optional Extras'],
  ['travelshoot_credit', 'Travelshoot Vouchers'],
  ['hotel_upgrade', 'Complimentary Hotel Upgrades'],
  ['esim_package', 'eSIM Data Pack'],
  ['family_and_friends_access', 'Family & Friends Access'],
]);

export function filterAccrualsForItem(
  itemId: string,
  accruals: Array<App.LuxLoyaltyAccrualItem>,
): Array<App.LuxLoyaltyAccrualItem> {
  return accruals.filter((acc) => acc.itemId === itemId);
}
